import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  CheckDuplicateEntityProposalPolicyQuery,
  DuplicateEntityProposalPolicyBody,
  EntityProposalPolicyApi,
  FindEntityProposalPolicyForInheritQuery,
  FindEntityProposalPolicyForSelectQuery,
  FindOptions,
  RemoveMultiChainEntityAndPartnerEntityProposalPolicy,
  UpdateEntityPricePolicyBody,
  UpdateEntityProposalPolicyForRetailBody,
  UpdateMultiChainEntityAndPartnerBody,
} from 'api';

export const findEntityProposalPolicyWithEntityForTableList = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyWithEntityForTableList',
  async (option: FindOptions, { rejectWithValue }) => {
    try {
      const response = await EntityProposalPolicyApi.findWithEntity(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/createEntityProposalPolicy',
  async (body: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await EntityProposalPolicyApi.create(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyForInherit = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyForInherit',
  async (
    option: FindEntityProposalPolicyForInheritQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProposalPolicyApi.findForInherit(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyForDuplicate = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyForDuplicate',
  async (
    option: FindEntityProposalPolicyForInheritQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProposalPolicyApi.findForDuplicate(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDefaultEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/findDefaultEntityProposalPolicy',
  async (_, { rejectWithValue }) => {
    try {
      const response = await EntityProposalPolicyApi.findDefault();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductionEntityProposalPolicy = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'entityProposalPolicy/findProductProposalPolicyEntityProposalPolicy',
  async (params: { id: string; option: FindOptions }, { rejectWithValue }) => {
    try {
      const { id, option } = params;

      const response = await EntityProposalPolicyApi.findProductProposal(
        id,
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const duplicateEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/duplicateEntityProposalPolicy',
  async (
    params: { id: string; body: DuplicateEntityProposalPolicyBody },
    { rejectWithValue }
  ) => {
    try {
      const { id, body } = params;
      const response = await EntityProposalPolicyApi.duplicate(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findInheritEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/findInheritEntityProposalPolicy',
  async (
    params: FindEntityProposalPolicyForInheritQuery,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await EntityProposalPolicyApi.findInheritEntityProposalPolicy(params);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyEntityProductForInherit = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyEntityProductForInherit',
  async (id: string, { rejectWithValue }) => {
    try {
      const response =
        await EntityProposalPolicyApi.findEntityProductForInherit(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyEntityProductForDuplicate =
  createAsyncThunk(
    'entityProposalPolicy/findEntityProposalPolicyEntityProductForDuplicate',
    async (id: string, { rejectWithValue }) => {
      try {
        const response =
          await EntityProposalPolicyApi.findEntityProductForDuplicate(id);

        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const findEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicy',
  async (option: FindOptions, { rejectWithValue }) => {
    try {
      const response = await EntityProposalPolicyApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/updateEntityProposalPolicy',
  async (
    params: { id: string; body: UpdateEntityPricePolicyBody },
    { rejectWithValue }
  ) => {
    try {
      const { id, body } = params;
      const response = await EntityProposalPolicyApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyForSelect = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyForSelect',
  async (
    params: FindEntityProposalPolicyForSelectQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProposalPolicyApi.findForSelect(params);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyForSelectInChain = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyForSelectInChain',
  async (
    params: FindEntityProposalPolicyForSelectQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProposalPolicyApi.findForSelectInChain(
        params
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyForSelectFromPartner = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyForSelectFromPartner',
  async (
    params: FindEntityProposalPolicyForSelectQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProposalPolicyApi.findForSelect(params);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/removeEntityProposalPolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityProposalPolicyApi.delete(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDuplicateEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/checkDuplicateEntityProposalPolicy',
  async (
    query: CheckDuplicateEntityProposalPolicyQuery,
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityProposalPolicyApi.checkDuplicate(query);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityProposalPolicyForRetail = createAsyncThunk(
  'entityProposalPolicy/findEntityProposalPolicyForRetail',
  async (any, { rejectWithValue }) => {
    try {
      const response =
        await EntityProposalPolicyApi.findEntityProposalPolicyForRetail();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntityProposalPolicyForRetail = createAsyncThunk(
  'entityProposalPolicy/updateEntityProposalPolicyForRetail',
  async (
    body: UpdateEntityProposalPolicyForRetailBody,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await EntityProposalPolicyApi.updateEntityProposalPolicyForRetail(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeMultiChainEntityAndPartnerEntityProposalPolicy =
  createAsyncThunk(
    'entityProposalPolicy/removeMultiChainEntityAndPartnerEntityProposalPolicy',
    async (
      body: RemoveMultiChainEntityAndPartnerEntityProposalPolicy,
      { rejectWithValue }
    ) => {
      try {
        const response =
          await EntityProposalPolicyApi.removeMultiChainEntityAndPartnerEntityProposalPolicy(
            body
          );

        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const updateMultiChainEntityAndPartnerEntityProposalPolicy =
  createAsyncThunk(
    'entityProposalPolicy/updateMultiChainEntityAndPartnerEntityProposalPolicy',
    async (body: UpdateMultiChainEntityAndPartnerBody, { rejectWithValue }) => {
      try {
        const response =
          await EntityProposalPolicyApi.updateMultiChainEntityAndPartnerEntityProposalPolicy(
            body
          );

        return response;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        return rejectWithValue(error.response.data);
      }
    }
  );

export const findOneEntityProposalPolicyWithEntity = createAsyncThunk(
  'entityProposalPolicy/findOneEntityProposalPolicyWithEntity',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityProposalPolicyApi.findOneWithEntity(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findApplyEntityInEntityProposalPolicy = createAsyncThunk(
  'entityProposalPolicy/findApplyEntityInEntityProposalPolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      const response =
        await EntityProposalPolicyApi.findApplyEntityInEntityProposalPolicy(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface InitialState {
  tableList: Array<any>;
  list: Array<any>;
}

const initialState: InitialState = {
  tableList: [],
  list: [],
};

const entityProposalPolicySlice = createSlice({
  name: 'entityProposalPolicy',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      findEntityProposalPolicyForSelect.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.list = newData;
      }
    );
  },
});

const { reducer: EntityProposalPolicyReducer } = entityProposalPolicySlice;

export default EntityProposalPolicyReducer;
